import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

// Import gallery images
import acdcLane from "@/assets/gallery/acdc-lane.jpg";
import beachSunset from "@/assets/gallery/beach-sunset.jpg";
import grottoArch from "@/assets/gallery/grotto-arch.jpg";
import melbourneCityview from "@/assets/gallery/melbourne-cityview.jpg";

interface GalleryImage {
  src: string;
  alt: string;
  category: "landmark" | "experience" | "settlement";
}

const PhotoGallery = () => {
  const isMobile = useIsMobile();
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [filter, setFilter] = useState<"all" | "landmark" | "experience" | "settlement">("all");
  const [showAll, setShowAll] = useState(false);

  const galleryImages: GalleryImage[] = [
    {
      src: acdcLane,
      alt: "Famous AC/DC Lane in Melbourne CBD",
      category: "landmark"
    },
    {
      src: beachSunset,
      alt: "Beautiful sunset at Melbourne beach with footprints in sand",
      category: "experience"
    },
    {
      src: grottoArch,
      alt: "Natural rock arch formation along the Great Ocean Road",
      category: "landmark"
    },
    {
      src: melbourneCityview,
      alt: "Melbourne city skyline view from above",
      category: "settlement"
    }
  ];

  const filteredImages = filter === "all" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === filter);

  // Show only 3 photos on mobile unless expanded
  const displayedImages = isMobile && !showAll 
    ? filteredImages.slice(0, 3) 
    : filteredImages;

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToNext = () => {
    if (selectedImage !== null && selectedImage < filteredImages.length - 1) {
      setSelectedImage(selectedImage + 1);
    }
  };

  const goToPrevious = () => {
    if (selectedImage !== null && selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
  };

  const filterCategories = [
    { value: "all" as const, label: "All Photos" },
    { value: "landmark" as const, label: "Melbourne Landmarks" },
    { value: "experience" as const, label: "Student Life" },
    { value: "settlement" as const, label: "Settlement Journey" }
  ];

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <header className="max-w-3xl mx-auto text-center space-y-3 md:space-y-4 mb-8 md:mb-12 animate-fade-in">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            Your Melbourne{" "}
            <span className="text-secondary">Journey</span>
          </h2>
          <p className="text-base md:text-xl text-muted-foreground">
            <span className="hidden md:inline">Discover student experiences, iconic landmarks, and settlement moments</span>
            <span className="md:hidden">Experiences & landmarks</span>
          </p>
        </header>

        {/* Filter Buttons - Simplified on mobile */}
        {!isMobile && (
          <div className="flex flex-wrap justify-center gap-3 mb-12 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            {filterCategories.map((category) => (
              <Button
                key={category.value}
                onClick={() => setFilter(category.value)}
                variant={filter === category.value ? "default" : "outline"}
                className="transition-all duration-300"
              >
                {category.label}
              </Button>
            ))}
          </div>
        )}

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-7xl mx-auto">
          {displayedImages.map((image, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-2xl cursor-pointer shadow-card hover:shadow-xl transition-all duration-500 hover:-translate-y-2 animate-scale-in"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => openLightbox(index)}
            >
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-48 md:h-64 object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                <p className="text-white text-sm font-medium">
                  {image.alt}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Show More Button on Mobile */}
        {isMobile && filteredImages.length > 3 && (
          <div className="text-center mt-6">
            <Button
              onClick={() => setShowAll(!showAll)}
              variant="outline"
              size="sm"
            >
              {showAll ? "Show Less" : `View ${filteredImages.length - 3} More Photos`}
            </Button>
          </div>
        )}

        {/* Lightbox */}
        <Dialog open={selectedImage !== null} onOpenChange={(open) => !open && closeLightbox()}>
          <DialogContent className="max-w-7xl w-full h-[90vh] p-0 bg-black/95 border-0">
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Close Button */}
              <Button
                onClick={closeLightbox}
                variant="ghost"
                size="icon"
                className="absolute top-4 right-4 z-50 text-white hover:bg-white/20"
              >
                <X className="w-6 h-6" />
              </Button>

              {/* Previous Button */}
              {selectedImage !== null && selectedImage > 0 && (
                <Button
                  onClick={goToPrevious}
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 z-50 text-white hover:bg-white/20"
                >
                  <ChevronLeft className="w-8 h-8" />
                </Button>
              )}

              {/* Next Button */}
              {selectedImage !== null && selectedImage < filteredImages.length - 1 && (
                <Button
                  onClick={goToNext}
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 z-50 text-white hover:bg-white/20"
                >
                  <ChevronRight className="w-8 h-8" />
                </Button>
              )}

              {/* Image */}
              {selectedImage !== null && (
                <div className="w-full h-full flex flex-col items-center justify-center p-8">
                  <img
                    src={filteredImages[selectedImage].src}
                    alt={filteredImages[selectedImage].alt}
                    className="max-w-full max-h-[calc(100%-80px)] object-contain rounded-lg"
                  />
                  <p className="text-white text-center mt-6 text-lg">
                    {filteredImages[selectedImage].alt}
                  </p>
                  <p className="text-white/60 text-center mt-2 text-sm">
                    {selectedImage + 1} / {filteredImages.length}
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default PhotoGallery;
